import api from '@/api';
import axios from 'axios';
import xml from '@/utils/xml';
import utils from '@/utils/utils';
import polylineInfo from '@/pages/preview/polyline';

/* global X2JS */
const x2js = new X2JS();

const state = {
  krp: null,
  worksId: 0,
  groupId: 0,
  groupList: [],
  sceneList: [],
  polylineList: [],
  sceneDetail: {},
  materialId: 0,
  isFirstGroup: false,
  tabActive: '',
};

const mutations = {
  SET_GROUP_LIST(state, value) {
    state.groupList = value;
    state.groupId = state.groupId || value[0].id;
    state.isFirstGroup = state.groupId == state.groupList[0].id;
  },

  SET_GROUP_ID(state, value) {
    state.groupId = value;
    state.isFirstGroup = state.groupId == state.groupList[0].id;
  },

  SET_SCENE_LIST(state, value) {
    state.sceneList = value;
    state.materialId = state.materialId || value[0].id;
  },

  SET_POLYLINE_LIST(state, value) {
    state.polylineList = value;
  },

  SET_MATERIAL_ID(state, value) {
    state.materialId = value;
    state.tabActive = '';
  },

  SET_SCENE_DETAIL(state, value) {
    state.sceneDetail = value;
  },

  SET_KRP_INSTANCE(state, value) {
    state.krp = value;
  },

  SET_WORKS_ID(state, value) {
    state.worksId = value;
  },

  SET_TAB_ACTIVE(state, value) {
    state.tabActive = value;
  },
};

const actions = {
  async getGroupList(context, worksId) {
    try {
      let res = await api.group.lists({ works_id: worksId });
      context.commit('SET_GROUP_LIST', res.data);
      context.commit('SET_WORKS_ID', worksId);
      return res.data;
    } catch (e) {
      console.log(e);
    }
  },

  async getSceneList(context, { worksId, groupId }) {
    try {
      let res = await api.works.getSceneList({ works_id: worksId, group_id: groupId });
      context.commit('SET_SCENE_LIST', res.data);
      return res.data;
    } catch (e) {
      console.log(e);
    }
  },

  async getSceneDetail(context, { worksId, materialId }) {
    try {
      let res = await api.works.getSceneDetail({ works_id: worksId, material_id: materialId });
      context.commit('SET_SCENE_DETAIL', res.data);
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },

  async getPolyList(context, { worksId, materialId }) {
    try {
      let res = await api.polyline.newLists({ works_id: worksId, material_id: materialId });
      context.commit('SET_POLYLINE_LIST', res.data);
      return res.data;
    } catch (e) {
      console.error(e);
    }
  },

  async loadKrp(context, { krp, sceneDetail }) {
    let krpJson = await context.dispatch('getKrpJsonData', sceneDetail);
    let xml = x2js.json2xml_str(krpJson);
    krp.call('loadxml(' + xml + ')');
    krp.call(`loadscene(scene_${sceneDetail.id})`);
  },

  //krp-instance
  getKrpInstance(context, domId) {
    return new Promise((resolve) => {
      /* global embedpano */
      embedpano({
        id: 'krpanoSWFObject',
        xml: '',
        target: domId,
        html5: 'only+webgl+preserveDrawingBuffer',
        mobilescale: 1.0,
        passQueryParameters: true,
        consolelog: true,
        onready: () => {
          let krpano = document.getElementById('krpanoSWFObject');
          context.commit('SET_KRP_INSTANCE', krpano);
          resolve(krpano);
        },
      });
    });
  },

  //krp-json-data
  getKrpJsonData(context, data) {
    return new Promise((resolve) => {
      let request = axios.create({});
      request({ url: process.env.BASE_URL + 'krpano/vr-admin-xml/index.xml', method: 'get' }).then(async (res) => {
        let xmlDoc = res.request.responseXML;
        let jsonXml = x2js.xml2json(xmlDoc);
        let scene = await context.dispatch('getKrpSceneData', data);
        jsonXml.krpano.scene = [scene];
        resolve(jsonXml);
      });
    });
  },

  //krp-scene-data
  async getKrpSceneData(context, data) {
    let scene = await context.dispatch('getSceneData', data);
    let hs = await context.dispatch('getHotspotData', data.spot_list);
    let lyEdit = await context.dispatch('getHotSpotLayerEdit', data.spot_list);
    let lyDel = await context.dispatch('getHotSpotLayerDelete', data.spot_list);
    let lyTitle = await context.dispatch('getHotSpotLayerTitle', data.spot_list);
    let hsLine = await context.dispatch('getPolylineData', data.point_list);
    let hsLineComment = await context.dispatch('getPolylineCommentData', data.point_list);
    let hsLineEdit = await context.dispatch('getPolylineEdit', data.point_list);
    scene.hotspot = [...hs, ...hsLine, ...hsLineComment];
    scene.layer = [...lyEdit, ...lyDel, ...lyTitle, ...hsLineEdit];
    return scene;
  },

  //json-scene
  getSceneData(context, data) {
    return xml.scene({
      material_id: data.material_id,
      stereographic: 'true',
      name: data.name,
      cube_url: data.path + '/%s/l%l/%v/l%l_%s_%v_%h.jpg',
      multires: data.resolution ? data.resolution : '512,640,1280',
      preview_url: data.preview,
      hlookatmin: data.info.hlookat_range ? data.info.hlookat_range[0] : -180,
      hlookat: data.info.hlookat_range ? data.info.hlookat_range[1] : 0,
      hlookatmax: data.info.hlookat_range ? data.info.hlookat_range[2] : 180,
      vlookatmin: data.info.vlookat_range ? data.info.vlookat_range[0] : -90,
      vlookat: data.info.vlookat_range ? data.info.vlookat_range[1] : process.env.VUE_APP_WEB == 2 ? 90 : 0,
      vlookatmax: data.info.vlookat_range ? data.info.vlookat_range[2] : 90,
      fovmin: data.info.fov_range ? data.info.fov_range[0] : 30,
      fov: data.info.fov_range ? data.info.fov_range[1] : 95,
      fovmax: data.info.fov_range ? data.info.fov_range[2] : 140,
    });
  },

  //json-scene-hotspot
  getHotspotData(context, data) {
    return data.map((item) => {
      if (item.thumb_preview === 'https://vybbasevr.vyb.cn/12.png') {
        item.width = item.height = 30;
      }
      return xml.hotspot({
        name: item.name,
        ath: item.ath,
        atv: item.atv,
        height: item.height,
        width: item.width,
        is_distorted: item.is_distorted == 1,
        is_zoom: item.is_zoom == 1,
        url: item.thumb_preview,
        title: item.title,
        ondown: context.state.isFirstGroup ? `draghotspot(${item.id})` : '',
        onover: context.state.isFirstGroup ? `overspot(${item.name})` : '',
        onout: context.state.isFirstGroup ? `outspot(${item.name})` : '',
      });
    });
  },

  //json-scene-hotspot-layer-edit
  getHotSpotLayerEdit(context, data) {
    return data.map((item) =>
      xml.layer({
        name: `${item.name}_edit`,
        html: `编辑`,
        align: 'righttop',
        visible: 'false',
        style: 'hotspot_label_style',
        x: '-36',
        parent: `hotspot[${item.name}]`,
        onclick: `js_edit_hotspot(${item.id})`,
      })
    );
  },

  //json-scene-hotspot-layer-delete
  getHotSpotLayerDelete(context, data) {
    return data.map((item) =>
      xml.layer({
        name: `${item.name}_delete`,
        html: `删除`,
        align: 'righttop',
        visible: 'false',
        style: 'hotspot_label_style',
        x: '-36',
        y: '20',
        parent: `hotspot[${item.name}]`,
        onclick: `js_delete_hotspot(${item.id},${item.name})`,
      })
    );
  },

  //json-scene-hotspot-layer-title
  getHotSpotLayerTitle(context, data) {
    return data.map((item) =>
      xml.layer({
        name: `${item.name}_title`,
        html: item.title,
        align: 'top',
        style: 'hotspot_label_style',
        visible: item.is_show_title == 1,
        y: '-25',
        parent: `hotspot[${item.name}]`,
      })
    );
  },

  //json-polyline-data
  getPolylineData(context, data) {
    return data.map((item) => {
			let color = polylineInfo[item.status].color16;
			color = item.kind === 1 ? utils.rgbaTo16('rgba(255, 255, 255, 1)').color : color;
			color = item.kind === 2 ? utils.rgbaTo16('rgba(147, 37, 156,1)').color : color;
    	return {
      _name: item.name,
      _type: 'image',
      _url: '',
      _keep: false,
      _visible: true,
      _zoom: false,
      _distorted: false,
      _borderwidth: item.width,
      _bordercolor: color,
      _polyline: true,
      _enabled: false,
      _handcursor: false,
      _scale: 20,
      _rotate: 60,
      point: item.points.map((item) => ({ _ath: item.ath, _atv: item.atv })),
    }
    });
  },

  //json-polyline-comment-data
  getPolylineCommentData(context, data) {
    let comment = data.map((item) => ({
      _name: item.name + '_comment',
      _type: 'text',
      _html: `${item.nickname}：${item.content.replace('系统检测','')} [br] ${item.create_time} `,
      _ath: item.comment_ath,
      _atv: item.comment_atv,
      _padding: 10,
      _oy: -30,
      _edge: 'bottom',
      _css: 'fontSize:12px',
      _bgcolor: utils.rgbaTo16('rgba(255,255,255,1)').color,
      _bgalpha: 0.6,
      _roundedge: 8,
      _handcursor: true,
      _zoom: false,
      _distorted: false,
      _enabled: true,
    }));

    let commentLine = data.map((item) => ({
      _name: item.name + '_position',
      _type: 'image',
      _url: '/krpano/images/preview/comment-line.png',
      _ath: item.comment_ath,
      _atv: item.comment_atv,
      _width: 20,
      _height: 60,
      _edge: 'bottom',
      _scale: 0.5,
      _alpha: 0.7,
      _enabled: false,
      _handcursor: false,
      _zoom: false,
      _distorted: false,
    }));
    return [...comment, ...commentLine];
  },

  //json-polyline-data-edit
  getPolylineEdit(context, data) {
    let arr = [];
    data.forEach((item) => {
      arr.push({
        _name: item.name + '_edit_container',
        _type: 'container',
        _keep: false,
        _visible: false,
        _zoom: false,
        _width: 185,
        _height: 30,
        _oy: -5,
        _align: 'top',
        _edge: 'bottom',
        _distorted: false,
        _ath: item.comment_ath,
        _atv: item.comment_atv,
        _background: false,
        _parent: `hotspot[${item.name}_comment]`,
        _enabled: true,
        _handcursor: true,
        _bgcapture: true,
        _onclick: `js(krpjs.handleEditPolyline(1,1))`,
      });
      // arr.push({
      //   _name: item.name + '_edit_scale_small',
      //   _type: 'text',
      //   _background: false,
      //   _html: '<i class="polyline-control iconfont icon-scale-small polyline-control-scale-mall"></i>',
      //   _parent: `layer[${item.name}_edit_container]`,
      //   _onclick: `js(krpjs.handleEditPolyline(${item.name},scale,small))`,
      // });
      // arr.push({
      //   _name: item.name + '_edit_scale_big',
      //   _type: 'text',
      //   _background: false,
      //   _ox: 35,
      //   _html: '<i class="polyline-control iconfont icon-scale-big polyline-control-scale-big"></i>',
      //   _parent: `layer[${item.name}_edit_container]`,
      //   _onclick: `js(krpjs.handleEditPolyline(${item.name},scale,big))`,
      // });
      // arr.push({
      //   _name: item.name + '_edit_rotate_right',
      //   _type: 'text',
      //   _background: false,
      //   _ox: 70,
      //   _html: '<i class="polyline-control iconfont icon-rotate-right polyline-control-rotate-right"></i>',
      //   _parent: `layer[${item.name}_edit_container]`,
      //   _onclick: `js(krpjs.handleEditPolyline(${item.name},rotate,right))`,
      // });
      // arr.push({
      //   _name: item.name + '_edit_rotate_left',
      //   _type: 'text',
      //   _background: false,
      //   _ox: 105,
      //   _html: '<i class="polyline-control iconfont icon-rotate-left polyline-control-rotate-left"></i>',
      //   _parent: `layer[${item.name}_edit_container]`,
      //   _onclick: `js(krpjs.handleEditPolyline(${item.name},rotate,left))`,
      // });
      arr.push({
        _name: item.name + '_edit_move_top',
        _type: 'text',
        _background: false,
        _ox: 0,
        _html: '<i class="polyline-control iconfont icon-arrow polyline-control-top"></i>',
        _parent: `layer[${item.name}_edit_container]`,
        _onclick: `js(krpjs.handleEditPolyline(${item.name},move,1))`,
      });
      arr.push({
        _name: item.name + '_edit_move_bottom',
        _type: 'text',
        _background: false,
        _ox: 35,
        _html: '<i class="polyline-control iconfont icon-arrow polyline-control-bottom"></i>',
        _parent: `layer[${item.name}_edit_container]`,
        _onclick: `js(krpjs.handleEditPolyline(${item.name},move,3))`,
      });
      arr.push({
        _name: item.name + '_edit_move_left',
        _type: 'text',
        _background: false,
        _ox: 70,
        _html: '<i class="polyline-control iconfont icon-arrow polyline-control-left"></i>',
        _parent: `layer[${item.name}_edit_container]`,
        _onclick: `js(krpjs.handleEditPolyline(${item.name},move,2))`,
      });
      arr.push({
        _name: item.name + '_edit_move_right',
        _type: 'text',
        _background: false,
        _ox: 105,
        _html: '<i class="polyline-control iconfont icon-arrow polyline-control-right"></i>',
        _parent: `layer[${item.name}_edit_container]`,
        _onclick: `js(krpjs.handleEditPolyline(${item.name},move,4))`,
      });
      arr.push({
        _name: item.name + '_edit_move',
        _type: 'text',
        _background: false,
        _ox: 140,
        _html: '<i class="polyline-control iconfont icon-move"></i>',
        _parent: `layer[${item.name}_edit_container]`,
        _ondown: `draghotspot2(${item.name})`,
      });
    });
    return arr;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
